import ITypedMap from '@/models/util/ITypedMap';
import RequestCache from '@/models/util/RequestCache';
import store from '@/store';
import TableProvider from './TableProvider';
import UiStringProvider, { IBulkRequestEntry } from './UiStringProvider';
import ICashShopEntry, { ICashShopTab } from '@/models/items/ICashShop';
import { mapCommodityRow, parseNeedJobClass, readTabs, sortEntries, tabNumToCategory } from '@/util/cashShop';

//  virt.cashcommodity is the merged commodity view (base cashcommodity +
//  cashcommodity_guild); always query the virt table.
const COMMODITY_TABLE = 'virt.cashcommodity';
const QUERY_LIMIT = 2000;

export interface ICashShopProvider {
    getTabs(region?: string): Promise<ICashShopTab[]>;
    getEntries(tabNum: number, subTabNum: number, includeOffSale: boolean, region?: string): Promise<ICashShopEntry[]>;
    getEntry(sn: number, region?: string): Promise<ICashShopEntry | null>;
    getEntriesBySns(sns: number[], region?: string): Promise<ICashShopEntry[]>;
    getPackageMemberSns(sn: number, region?: string): Promise<number[]>;
    findByItemIds(itemIds: number[], region?: string): Promise<ICashShopEntry[]>;
    getNeedJobClasses(itemIds: number[], region?: string): Promise<ITypedMap<number[]>>;
}

class CashShopProvider implements ICashShopProvider {
    private _tabsCache: ITypedMap<ICashShopTab[]> = {};
    private _tabsReqCache = new RequestCache<ICashShopTab[]>();
    private _entriesCache: ITypedMap<ICashShopEntry[]> = {};
    private _entriesReqCache = new RequestCache<ICashShopEntry[]>();
    private _needJobCache: ITypedMap<number[]> = {};

    public async getTabs(region?: string): Promise<ICashShopTab[]> {
        region = this._ensureRegion(region);
        const cached = this._tabsCache[region];
        if (cached) {
            return cached;
        }

        return this._tabsReqCache.tryCache(region, async () => {
            const rows = await TableProvider.getTable<any>('cashtabtable', region);
            const tabs = readTabs(rows);

            const requests: IBulkRequestEntry[] = [];
            for (const tab of tabs) {
                if (tab.nameId) {
                    requests.push({ id: tab.nameId });
                }
                for (const sub of tab.subTabs) {
                    requests.push({ id: sub.nameId });
                }
            }

            const resolved = await UiStringProvider.getBulk(requests, region);
            for (const tab of tabs) {
                tab.name = resolved[tab.nameId]?.message || `#${tab.nameId}`;
                for (const sub of tab.subTabs) {
                    sub.name = resolved[sub.nameId]?.message || `#${sub.nameId}`;
                }
            }

            this._tabsCache[region!] = tabs;
            return tabs;
        });
    }

    public async getEntries(tabNum: number, subTabNum: number, includeOffSale: boolean, region?: string): Promise<ICashShopEntry[]> {
        region = this._ensureRegion(region);
        const key = `${region}:${tabNum}:${subTabNum}:${includeOffSale ? 1 : 0}`;
        const cached = this._entriesCache[key];
        if (cached) {
            return cached;
        }

        return this._entriesReqCache.tryCache(key, async () => {
            const category = tabNumToCategory(tabNum);
            let q = `_Category;eq;${category}$_SubCategory;eq;${subTabNum}`;
            if (!includeOffSale) {
                q += '$_OnSale;eq;1';
            }

            const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, q, region, {
                uiresolve: ['_NameID'],
                limit: QUERY_LIMIT,
            });

            const entries = sortEntries(rows.map(mapCommodityRow));
            this._entriesCache[key] = entries;
            return entries;
        });
    }

    public async getEntry(sn: number, region?: string): Promise<ICashShopEntry | null> {
        region = this._ensureRegion(region);
        const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, `_SN;eq;${sn}`, region, {
            uiresolve: ['_NameID', '_DescriptionID'],
            limit: 2,
        });

        return rows.length ? mapCommodityRow(rows[0]) : null;
    }

    public async getEntriesBySns(sns: number[], region?: string): Promise<ICashShopEntry[]> {
        if (!sns.length) {
            return [];
        }

        region = this._ensureRegion(region);
        const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, `_SN;in;${sns.join(',')}`, region, {
            uiresolve: ['_NameID'],
            limit: QUERY_LIMIT,
        });

        const bySn: ITypedMap<ICashShopEntry> = {};
        for (const row of rows) {
            const entry = mapCommodityRow(row);
            bySn[entry.sn] = entry;
        }

        return sns.map((sn) => bySn[sn]).filter((e) => !!e);
    }

    public async getPackageMemberSns(sn: number, region?: string): Promise<number[]> {
        region = this._ensureRegion(region);
        const rows = await TableProvider.queryTable<any>('cashpackagetable', `_SN;eq;${sn}`, region, {
            limit: 2,
        });
        if (!rows.length) {
            return [];
        }

        const out: number[] = [];
        for (let i = 0; i <= 19; i++) {
            const member = Number(rows[0]['_CommodityID' + String(i).padStart(2, '0')]);
            if (member) {
                out.push(member);
            }
        }

        return out;
    }

    public async findByItemIds(itemIds: number[], region?: string): Promise<ICashShopEntry[]> {
        if (!itemIds.length) {
            return [];
        }

        region = this._ensureRegion(region);
        //  Chunk to keep query URLs bounded.
        const out: ICashShopEntry[] = [];
        for (let i = 0; i < itemIds.length; i += 50) {
            const chunk = itemIds.slice(i, i + 50);
            const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, `_ItemID01;in;${chunk.join(',')}`, region, {
                uiresolve: ['_NameID'],
                limit: QUERY_LIMIT,
            });
            out.push(...rows.map(mapCommodityRow));
        }

        return sortEntries(out);
    }

    //  Batched _NeedJobClass lookup for class filtering: itemId -> job id list
    //  ([] = usable by every class, including items with no itemtable row).
    public async getNeedJobClasses(itemIds: number[], region?: string): Promise<ITypedMap<number[]>> {
        region = this._ensureRegion(region);
        const ret: ITypedMap<number[]> = {};
        const missing: number[] = [];
        for (const id of itemIds) {
            const cached = this._needJobCache[`${region}:${id}`];
            if (cached) {
                ret[id] = cached;
            } else if (id) {
                missing.push(id);
            }
        }

        for (let i = 0; i < missing.length; i += 100) {
            const chunk = missing.slice(i, i + 100);
            let rows: ITypedMap<any> = {};
            try {
                if (chunk.length === 1) {
                    //  A single id returns the bare row, not an id-keyed map.
                    rows = { [chunk[0]]: await TableProvider.getTableRow<any>('virt.itemtable', chunk[0], region, { select: ['_NeedJobClass'] as any }) };
                } else {
                    rows = await TableProvider.getTableRows<any>('virt.itemtable', chunk, region, { select: ['_NeedJobClass'] as any });
                }
            } catch (e) {
                //  Missing rows fall through to the usable-by-all default below.
            }

            for (const id of chunk) {
                const row = rows[id];
                const parsed = parseNeedJobClass(row ? String(row._NeedJobClass || '') : '');
                this._needJobCache[`${region}:${id}`] = parsed;
                ret[id] = parsed;
            }
        }

        return ret;
    }

    private _ensureRegion(region?: string): string {
        if (!region) {
            return store.state.regionCode;
        }

        return region;
    }
}

export default new CashShopProvider() as ICashShopProvider;
